/**
 * DemoCompanySwitcher — the top-bar company/org identity picker. Replaces the
 * static demo-company badge with a dropdown that switches the company identity
 * IN PLACE: the current page stays mounted and re-renders with the new
 * company's data (no round-trip to the home picker).
 *
 * Modes (tunnel model — every mode has a top-bar org affordance):
 *   demo                  → this dropdown, labeled "Demo"
 *   development / quality → this dropdown too, labeled "Organization" — the
 *     same company identities the homepage offers in those modes (b9628711
 *     "Industry Profile"/"Organization" pickers), switchable without leaving
 *     the module page (Connor 2026-07-18: "development can't change in the
 *     module page"). Identity continuity is P2-lite: labels + company scope
 *     carry; dev/quality data-level org scoping lands with the P1 collapse.
 *   sandbox               → TunnelOrgChip (curated REAL orgs, in place)
 *   production            → ActiveOrgSwitcher (amber)
 * Internal surface only — a client preview must never expose other companies.
 *
 * Switching mirrors the home picker's pickCompany exactly (companyPickIntent →
 * persona re-cast, currency reset via setCurrentCompany, root-portfolio scope),
 * plus one extra step the home picker doesn't need: aligning the demo PROFILE when
 * the target company lives under a different industry (e.g. Gentex → Codan).
 */
import { useState, useRef, useEffect } from 'react'
import { Building2, ChevronDown, Check } from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { usePortfolios } from '@/hooks/usePortfolioData'
import { DEMO_PROFILES } from '@/types/demoProfiles'
import { companiesForProfile, getDemoCompany, scopePortfoliosToCompany } from '@/types/demoCompanies'
import { markCompanyPicked } from '@/modules/timesheet/lib/companyPickIntent'
import { isInternalSurface } from '@/config/productSurface'
import { cn } from '@/lib/utils'

export function DemoCompanySwitcher() {
  const appMode = useAppStore((s) => s.appMode)
  const demoProfileId = useAppStore((s) => s.demoProfileId)
  const setDemoProfile = useAppStore((s) => s.setDemoProfile)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const setCurrentCompany = useAppStore((s) => s.setCurrentCompany)
  const selectPortfolio = useAppStore((s) => s.selectPortfolio)
  const { data: portfolios } = usePortfolios()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Keep the portfolio breadcrumb consistent with the active company on EVERY
  // company-change path, not just picks made from this dropdown. Picking a
  // persona from another company's cast (applyPersona), the refresh-restore
  // reconcile, and timesheet-only entry all change/restore the company WITHOUT
  // re-scoping the selected portfolio — leaving either a stale cross-company
  // breadcrumb or none at all. If the selected portfolio isn't in the active
  // company's subtree (or nothing is selected), scope to the company's root.
  // The company-identity modes this dropdown serves (sandbox/production have
  // their own real-org switchers).
  const identityMode = appMode === 'demo' || appMode === 'development' || appMode === 'quality'
  const isDemo = appMode === 'demo'

  useEffect(() => {
    if (appMode !== 'demo' && appMode !== 'development' && appMode !== 'quality') return
    const company = getDemoCompany(currentCompanyId)
    if (!company?.rootPortfolioId || !portfolios || portfolios.length === 0) return
    const selected = useAppStore.getState().selectedPortfolio
    if (selected && scopePortfoliosToCompany(portfolios, company.id).some((p) => p.id === selected.id)) return
    const root = portfolios.find((p) => p.id === company.rootPortfolioId)
    if (root) selectPortfolio(root)
  }, [appMode, currentCompanyId, portfolios, selectPortfolio])

  if (!identityMode || !isInternalSurface()) return null

  const active = getDemoCompany(currentCompanyId)

  function pickCompany(companyId: string) {
    setOpen(false)
    if (companyId === currentCompanyId) return
    const company = getDemoCompany(companyId)
    // Deliberate switch → the persona reconcile re-points to the new company's cast.
    markCompanyPicked(companyId)
    // Cross-industry jump: align the demo profile first (clears caches + re-signs-in),
    // then scope to the company — same end state as picking it from the home page.
    if (company && company.profileId !== demoProfileId) setDemoProfile(company.profileId)
    setCurrentCompany(companyId)
    // Re-scope IN PLACE: select the new company's root portfolio (which also
    // restores contextSelected after a cross-profile reset, all within this one
    // handler → single render, so the current route never unmounts). No navigation
    // — the page you're on just re-renders with the new company's data.
    if (company) {
      const root = (portfolios ?? []).find((p) => p.id === company.rootPortfolioId)
      if (root) selectPortfolio(root)
    }
  }

  function clearCompany() {
    setOpen(false)
    if (currentCompanyId == null) return
    setCurrentCompany(null)
  }

  // min-w-0 (not flex-shrink-0): on phone widths the chip must SHRINK inside
  // the header's flex-1 middle slot instead of overflowing into the search
  // button (mobile banner overlap, Connor 2026-07-25).
  return (
    <div ref={ref} className="relative min-w-0">
      <button
        onClick={() => setOpen((v) => !v)}
        title={
          active
            ? isDemo
              ? `Demo: ${active.name} — click to switch demos`
              : `Organization: ${active.name} — click to switch`
            : isDemo
              ? 'Switch demo company'
              : 'Switch organization'
        }
        aria-label={isDemo ? 'Switch demo company' : 'Switch organization'}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-transparent text-text-secondary text-body-sm font-medium hover:border-border hover:bg-surface-muted active:scale-95 transition-colors"
      >
        <Building2 size={14} className="flex-shrink-0 text-brand-500" />
        <span className="max-w-[110px] sm:max-w-[160px] truncate">{active?.name ?? (isDemo ? 'All demo companies' : 'All organizations')}</span>
        <ChevronDown size={13} className={`flex-shrink-0 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="fixed inset-x-2 top-14 sm:absolute sm:inset-x-auto sm:left-0 sm:top-11 sm:w-80 max-h-[85vh] overflow-y-auto bg-white rounded-lg shadow-dropdown border border-border py-1 animate-slide-in-up z-50">
          <div className="px-3 py-2 border-b border-border">
            <div className="text-body-sm font-semibold text-text-primary">{isDemo ? 'Switch demo' : 'Switch organization'}</div>
            <div className="text-[11px] text-text-tertiary">
              {isDemo
                ? 'One demo per company — switching re-scopes data, personas, and currency'
                : 'Switching re-scopes the workspace to that organization, in place'}
            </div>
          </div>

          <button
            onClick={clearCompany}
            className={cn(
              'w-full text-left px-3 py-2 flex items-center gap-2 transition-colors',
              currentCompanyId == null ? 'bg-surface-muted' : 'hover:bg-surface-muted/50'
            )}
          >
            <Building2 size={14} className="flex-shrink-0 text-text-tertiary" />
            <span className="flex-1 text-body-sm text-text-primary">All companies (unscoped)</span>
            {currentCompanyId == null && <Check size={14} className="flex-shrink-0 text-brand-600" />}
          </button>

          {DEMO_PROFILES.map((profile) => {
            const companies = companiesForProfile(profile.id)
            if (companies.length === 0) return null
            return (
              <div key={profile.id}>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-text-tertiary border-t border-border mt-1">
                  {profile.industry}
                </div>
                {companies.map((company) => {
                  const isActive = company.id === currentCompanyId
                  return (
                    <button
                      key={company.id}
                      onClick={() => pickCompany(company.id)}
                      className={cn(
                        'w-full text-left px-3 py-2 flex items-start gap-2 transition-colors',
                        isActive ? 'bg-brand-50' : 'hover:bg-surface-muted/50'
                      )}
                    >
                      <Building2 size={14} className={cn('flex-shrink-0 mt-0.5', isActive ? 'text-brand-600' : 'text-text-tertiary')} />
                      <div className="flex-1 min-w-0">
                        <div className={cn('text-body-sm font-medium', isActive ? 'text-brand-700' : 'text-text-primary')}>
                          {company.name}
                        </div>
                        <div className="text-[11px] text-text-tertiary leading-tight mt-0.5 line-clamp-2">{company.blurb}</div>
                      </div>
                      {isActive && <Check size={14} className="flex-shrink-0 mt-0.5 text-brand-600" />}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
